import React, { useState, useEffect } from "react";
import { Loader } from "./components/Loader";
import { useFontAwesome } from "./hooks/useFontAwesome";
import { uploadToMinio } from "./OSModal";
import { ENDPOINTS } from "./endpoints";

const placeholderImage = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
const imagemPadrao = "https://cdn-icons-png.flaticon.com/512/2748/2748558.png";

export default function OSModalNew({ onClose, unidade, onCriada }: { onClose: () => void, unidade: string, onCriada?: () => void }) {
  useFontAwesome();
  const [departamentos, setDepartamentos] = useState<{id: number, descricao: string}[]>([]);
  const [departamento, setDepartamento] = useState("");
  const [local, setLocal] = useState("");
  const [solicitante, setSolicitante] = useState("");
  const [ocorrencia, setOcorrencia] = useState("");
  const [fotos, setFotos] = useState<{ id: number; localUrl: string; file: File | null }[]>([
    { id: 1, localUrl: "", file: null },
    { id: 2, localUrl: "", file: null },
    { id: 3, localUrl: "", file: null },
  ]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ open: boolean, message: string, type?: 'error' | 'success' }>({ open: false, message: '', type: 'error' });

  useEffect(() => {
    fetch(ENDPOINTS.DEPARTAMENTO_LIST, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ unidade })
    })
      .then(res => res.json())
      .then(data => setDepartamentos(data));
  }, [unidade]);

  function handleFotoChange(idx: number, file: File) {
    const url = URL.createObjectURL(file);
    setFotos(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: url, file } : f));
  }
  function handleFotoLimpar(idx: number) {
    setFotos(fotos => fotos.map((f, i) => i === idx ? { ...f, localUrl: "", file: null } : f));
  }

  async function handleGravar() {
    if (!departamento || !local || !solicitante || !ocorrencia) {
      setToast({ open: true, message: 'Preencha todos os campos obrigatórios!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      return;
    }
    setLoading(true);
    try {
      // Primeiro criar a OS
      const endpoint = `https://automation.floripasoftware.com.br/webhook/saveos_${(unidade || '').toLowerCase()}`;
      const payload = {
        departamento,
        local,
        solicitante,
        ocorrencia,
        foto_url: "" // Fotos serão integradas depois
      };
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      
      let osId: number | undefined = undefined;
      if (data && data.retorno) {
        try {
          const parsed = JSON.parse(data.retorno);
          osId = parsed.os_id;
        } catch (e) {
          osId = undefined;
        }
      } else if (Array.isArray(data) && data[0]?.success) {
        osId = data[0].os_id;
      }
      
      if (osId) {
        // Agora fazer upload das fotos se houver
        const fotosComArquivo = fotos.filter(f => f.file);
        if (fotosComArquivo.length > 0) {
          for (const foto of fotosComArquivo) {
            if (foto.file) {
              try {
                await uploadToMinio({
                  file: foto.file,
                  unidade,
                  tipo: 'chamado',
                  osId,
                  fotoNum: foto.id
                });
              } catch (err) {
                console.error('Erro ao fazer upload da foto:', err);
                // Continua mesmo se der erro em alguma foto
              }
            }
          }
        }
        setToast({ open: true, message: 'OS criada com sucesso!', type: 'success' });
        setTimeout(() => {
          setToast(t => ({ ...t, open: false }));
          if (onCriada) onCriada();
          onClose();
        }, 1200);
      } else {
        setToast({ open: true, message: 'Erro ao criar OS!', type: 'error' });
        setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
      }
    } catch (err) {
      setToast({ open: true, message: 'Erro de conexão ao criar OS!', type: 'error' });
      setTimeout(() => setToast(t => ({ ...t, open: false })), 3000);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {toast.open && (
        <div className={`fixed top-6 left-1/2 z-[9999] -translate-x-1/2 px-6 py-3 rounded shadow-lg text-white font-semibold transition-all ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>{toast.message}</div>
      )}
      {loading && (
        <>
          <Loader />
          <div className="fixed top-24 left-1/2 z-[9999] -translate-x-1/2 px-8 py-4 rounded shadow-lg text-white font-semibold transition-all bg-blue-700 text-center" style={{minWidth: 320, maxWidth: 480}}>
            Aguarde, estamos cadastrando a OS e notificando os validadores.<br />
            Isso pode levar alguns segundos...
          </div>
        </>
      )}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-[98vw] mx-2 max-h-[90vh] overflow-y-auto relative px-2 sm:px-6">
          <div className="flex flex-wrap items-center justify-between border-b px-6 py-4 bg-gray-100 relative">
            <span className="font-bold text-lg text-black">Nova Ordem de Serviço</span>
            <button
              onClick={onClose}
              className="absolute top-2 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white shadow hover:bg-red-100 transition-colors border border-gray-200"
              aria-label="Fechar"
              title="Fechar"
            >
              <span className="text-2xl text-gray-700 hover:text-red-600 font-bold pointer-events-none">×</span>
            </button>
          </div>
          <div className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700">Departamento</label>
                <select
                  className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0"
                  value={departamento}
                  onChange={e => setDepartamento(e.target.value)}
                >
                  <option value="">Selecione o departamento</option>
                  {departamentos.map(dep => (
                    <option key={dep.id} value={dep.descricao}>{dep.descricao}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700">Descrição do Local</label>
                <input
                  type="text"
                  className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0"
                  value={local}
                  onChange={e => setLocal(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700">Nome do Solicitante</label>
                <input
                  type="text"
                  className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0"
                  value={solicitante}
                  onChange={e => setSolicitante(e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-gray-700">Descrição do Chamado</label>
                <textarea
                  className="w-full bg-yellow-100 border border-yellow-300 p-2 m-0 text-base text-black focus:outline-none focus:ring-0 resize-none"
                  rows={3}
                  value={ocorrencia}
                  onChange={e => setOcorrencia(e.target.value)}
                />
              </div>
            </div>
            {/* Upload de fotos */}
            <div className="mt-4">
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t font-bold">FOTOS referente ao SERVIÇO a ser executado</div>
              <div className="border border-t-0 rounded-b p-4 grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50">
                {fotos.map((foto, idx) => (
                  <div key={foto.id} className="flex flex-col items-center">
                    <div className="w-full h-72 bg-gray-200 rounded flex items-center justify-center overflow-hidden relative">
                      <img
                        src={foto.localUrl || imagemPadrao}
                        alt={`Foto ${idx+1}`}
                        width={200}
                        height={150}
                        className="object-contain h-full cursor-pointer"
                        style={{ maxWidth: '100%', maxHeight: '100%' }}
                        onError={e => (e.currentTarget.src = imagemPadrao)}
                      />
                      <div className="absolute top-2 right-2 flex flex-col gap-2 z-20">
                        <label className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow cursor-pointer flex items-center justify-center" title="Enviar foto">
                          <i className="fa fa-camera"></i>
                          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => e.target.files && e.target.files[0] && handleFotoChange(idx, e.target.files[0])} />
                        </label>
                        <button className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow flex items-center justify-center cursor-pointer" title="Limpar foto" onClick={() => handleFotoLimpar(idx)}>
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </div>
                    <span className="mt-2 text-xs font-semibold">FOTO[{foto.id}]</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg flex justify-end">
            <button
              className={`bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded font-bold cursor-pointer mr-4 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
              onClick={handleGravar}
              disabled={loading}
            >
              {loading ? 'Gravando...' : 'GRAVAR'}
            </button>
            <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded font-bold">FECHAR</button>
          </div>
        </div>
      </div>
    </>
  );
} 